// Google Drive picker + recursive folder walk + concurrent download
// helpers for the DrivePickerPanel. Ports the legacy `frontend/
// app.jsx` Drive helpers (lines 1958-2296) to TS.
//
// Architecture: thin module of `fetch` wrappers + the Google
// Picker SDK shim. We hit Drive's REST API directly with the
// operator's access token; nothing goes through the InRange
// backend. The output of `downloadDriveFileAsBlob` is a `File`
// the caller hands to the existing /uploads/initiate -> S3 PUT ->
// /uploads/complete flow, so imported files end up alongside
// drag-dropped + Gmail-imported files with content_type tagged
// from the file's MIME (or the export target for Google-native
// types).
import { GOOGLE_API_KEY } from "./google-config";
import { type PickerDoc } from "./google-auth";

export type { PickerDoc };

// ---------------------------------------------------------------------- //
// Constants
// ---------------------------------------------------------------------- //
export const FOLDER_MIME = "application/vnd.google-apps.folder";
export const MAX_FOLDER_DEPTH = 5;

// Default per-file size cap. Configurable in the preview UI before
// the operator clicks Confirm. Anything larger is moved to the
// "too big" bucket.
//
// MUST stay <= MAX_UPLOAD_BYTES in src/inrange_agents/uploads.py
// (currently 500 MB). Files above MULTIPART_THRESHOLD_BYTES
// (25 MB) take the multipart pipeline; below it the simpler
// single-PUT path. Files between this cap and the backend cap
// would pass FE preview, get a presigned URL flow, upload to S3,
// then 413 at completion - leaving an orphan S3 object and a
// confusing toast. Keep this number in lock-step with the
// backend cap; the dropdown options offered in the preview UI
// don't exceed it.
export const DEFAULT_SIZE_CAP_MB = 500;

// Google-native MIME types -> ordered list of export-format
// candidates. Office-equivalent formats come first so the imported
// file preserves the source's structure (multiple sheets, formulas,
// cell types, document sections, styles) rather than the flattened
// PDF rasterisation Google's PDF exporter produces. The backend
// extractors (openpyxl, python-docx) read these formats natively.
//
// Google enforces a 10 MB hard cap on PDF export (and similar caps
// on the Office targets) and large Docs / Sheets / Slides trip it
// (`exportSizeLimitExceeded`). When the Office format itself
// exceeds the cap we fall through to PDF, then to plain text / CSV
// which compresses far smaller.
//
// Note: Slides export as .pptx for fidelity, but the backend does
// not yet have a pptx extractor - agent runs that try to read an
// imported Slides file will fail until one is added. The bytes
// are preserved in S3 either way, so operators can still view the
// file from the files-management page.
const NATIVE_EXPORT_MAP: Record<string, { mime: string; ext: string }[]> = {
  "application/vnd.google-apps.document": [
    {
      mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ext: ".docx",
    },
    { mime: "application/pdf", ext: ".pdf" },
    { mime: "text/plain", ext: ".txt" },
  ],
  "application/vnd.google-apps.spreadsheet": [
    {
      mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ext: ".xlsx",
    },
    { mime: "application/pdf", ext: ".pdf" },
    { mime: "text/csv", ext: ".csv" },
  ],
  "application/vnd.google-apps.presentation": [
    {
      mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ext: ".pptx",
    },
    { mime: "application/pdf", ext: ".pdf" },
    { mime: "text/plain", ext: ".txt" },
  ],
  "application/vnd.google-apps.drawing": [
    { mime: "image/png", ext: ".png" },
    { mime: "application/pdf", ext: ".pdf" },
  ],
  "application/vnd.google-apps.script": [
    { mime: "application/vnd.google-apps.script+json", ext: ".json" },
  ],
};

const SKIP_EXPORT_TYPES = new Set<string>([
  "application/vnd.google-apps.form",
  "application/vnd.google-apps.site",
  "application/vnd.google-apps.map",
  "application/vnd.google-apps.fusiontable",
]);

// File types the agent's extractors can actually read. Anything
// else is filtered out before download so we don't burn browser
// memory + S3 budget on bytes the agent will ignore (the typical
// Inrange project folder is dominated by drone imagery, CAD files,
// and video walkthroughs, which today's pypdf + python-docx +
// openpyxl extractors can't open).
const AGENT_READABLE_EXTS = new Set([
  "pdf", "docx", "doc", "xlsx", "xls", "csv", "txt", "png", "jpg", "jpeg",
  // A picked .zip is downloaded then uploaded like any file; the backend expands
  // it into its member documents at /uploads/complete (see uploads._maybe_expand_zip).
  "zip",
]);
const AGENT_READABLE_NATIVE_MIMES = new Set([
  // Anything in NATIVE_EXPORT_MAP - we can export these.
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation",
  "application/vnd.google-apps.drawing",
]);

// File instances produced by `downloadDriveFileAsBlob` are tagged
// with the original Drive file id so the upload pipeline can carry
// `source = "drive"` + `drive_file_id` through to the backend. The
// "Refresh from Drive" action on the files-management page uses
// this id to re-fetch the latest bytes via Drive's API. Hidden
// behind a Symbol key so the standard File API and serialisation
// paths (FormData, structured clone) ignore them.
const DRIVE_FILE_ID = Symbol("inrange.driveFileId");
const DRIVE_SOURCE = Symbol("inrange.driveSource");
const DRIVE_PATH = Symbol("inrange.drivePath");
const DRIVE_MODIFIED_TIME = Symbol("inrange.driveModifiedTime");

interface TaggedFile extends File {
  [DRIVE_FILE_ID]?: string;
  [DRIVE_SOURCE]?: "drive";
  [DRIVE_PATH]?: string;
  [DRIVE_MODIFIED_TIME]?: string;
}

export const tagDriveFile = (
  file: File,
  driveFileId: string,
  // The Drive folder path + RFC-3339 modified time, carried onto the
  // downloaded File so the duplicate-resolver modal can still show
  // them after download (a plain File loses both - its `lastModified`
  // is the download time, not Drive's).
  meta?: { path?: string; modifiedTime?: string },
): File => {
  const tagged = file as TaggedFile;
  tagged[DRIVE_FILE_ID] = driveFileId;
  tagged[DRIVE_SOURCE] = "drive";
  if (meta?.path) tagged[DRIVE_PATH] = meta.path;
  if (meta?.modifiedTime) tagged[DRIVE_MODIFIED_TIME] = meta.modifiedTime;
  return tagged;
};

export const getDriveFileId = (file: File): string | undefined =>
  (file as TaggedFile)[DRIVE_FILE_ID];

export const getDriveSource = (file: File): "drive" | undefined =>
  (file as TaggedFile)[DRIVE_SOURCE];

export const getDrivePath = (file: File): string | undefined =>
  (file as TaggedFile)[DRIVE_PATH];

export const getDriveModifiedTime = (file: File): string | undefined =>
  (file as TaggedFile)[DRIVE_MODIFIED_TIME];

// ---------------------------------------------------------------------- //
// Errors
// ---------------------------------------------------------------------- //

// Soft-skip: the file exists in Drive but isn't downloadable for
// reasons we can't fix from the FE (Forms / Sites / Maps don't
// export; Drawings that exceed every export-format size cap).
// Distinguished from hard failures (network, auth, server 5xx) so
// the concurrent runner can report them separately.
export class DriveSkippedError extends Error {
  readonly skipped = true as const;
  constructor(message: string) {
    super(message);
    this.name = "DriveSkippedError";
  }
}

// ---------------------------------------------------------------------- //
// Picker
// ---------------------------------------------------------------------- //

// Open the Google Picker with two views (files + folders), both
// folder-selectable. `onPicked` fires when the operator confirms;
// the picker auto-closes after. Resolves the returned Promise to
// the picked docs (file/folder metadata) OR null on cancel.
export const openDrivePicker = (accessToken: string): Promise<PickerDoc[] | null> => {
  return new Promise((resolve, reject) => {
    if (!window.google?.picker) {
      reject(new Error("Google Picker SDK not loaded - call ensureGapiPickerLoaded() first."));
      return;
    }
    const picker = window.google.picker;
    const filesView = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true);
    const foldersView = new picker.DocsView(picker.ViewId.FOLDERS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(FOLDER_MIME);
    new picker.PickerBuilder()
      .setOAuthToken(accessToken)
      .setDeveloperKey(GOOGLE_API_KEY)
      .addView(filesView)
      .addView(foldersView)
      .setCallback((data) => {
        // Picker emits the PICKED action when the operator
        // confirms; CANCEL when they close without picking. We
        // treat any other action (loaded/etc.) as no-op.
        if (data.action === picker.Action.PICKED) {
          resolve(data.docs ?? []);
        } else if (data.action === picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .build()
      .setVisible(true);
  });
};

// ---------------------------------------------------------------------- //
// Drive listing
// ---------------------------------------------------------------------- //

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string; // Drive returns this as a string of bytes
  parents?: string[];
  shortcutDetails?: { targetId?: string; targetMimeType?: string };
  // RFC-3339 last-modified timestamp from Drive. Surfaced in the
  // duplicate-filename resolver so the operator can tell which version
  // is the latest.
  modifiedTime?: string;
  // Human-readable folder path within the picked tree, e.g.
  // "MME1 Docs/Designs/SLD.pdf". Built by listFolderRecursively as it
  // walks; absent for directly-picked files (path is just the name).
  // Lets the duplicate resolver disambiguate same-named files by
  // location.
  path?: string;
}

// Direct children only. Returns BOTH files and sub-folders - the
// caller recurses into sub-folders separately.
//
// supportsAllDrives + includeItemsFromAllDrives + corpora=allDrives
// make Shared Drives and shared-with-me folders visible. Without
// them the API silently returns 0 hits for anything outside the
// operator's own My Drive.
//
// `size` lets the preview panel show byte totals + per-file cap
// warnings BEFORE any download starts. Google-native types
// (Docs / Sheets / Slides) don't carry a size in Drive metadata -
// they come back without that field and the classifier falls
// through to `unknown size`.
// ---------------------------------------------------------------------- //
// Transient-error backoff: retry rate-limit / 5xx Drive responses
// ---------------------------------------------------------------------- //

const DRIVE_MAX_ATTEMPTS = 4;
const DRIVE_BACKOFF_BASE_MS = 500;

// Surfaced before each backoff wait so the caller can message the user
// ("Drive busy, retrying…") rather than hard-failing the file/walk.
export interface DriveRetryNotice {
  attempt: number;
  maxAttempts: number;
  delayMs: number;
  status: number;
}
export type OnDriveRetry = (notice: DriveRetryNotice) => void;

const driveSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// 429 (rate / user-rate) + 5xx are transient and retried. 401 (token) and 403
// are terminal here: 401 is the caller's token-refresh path, and a 403 may be a
// genuine permission error OR Drive's exportSizeLimitExceeded fall-through
// (downloadDriveFileAsBlob inspects the 403 body itself), so we never retry it.
const isRetryableDriveStatus = (status: number): boolean =>
  status === 429 || (status >= 500 && status < 600);

const driveRetryDelayMs = (resp: Response, attempt: number): number => {
  const header = resp.headers.get("Retry-After");
  if (header) {
    const secs = Number(header);
    if (Number.isFinite(secs)) return Math.max(0, secs * 1000);
    const dateMs = Date.parse(header);
    if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now());
  }
  const base = DRIVE_BACKOFF_BASE_MS * 2 ** (attempt - 1);
  return Math.round(base * (1 + Math.random() * 0.5));
};

// GET a Drive endpoint with the operator's token, retrying transient
// rate-limit / 5xx responses with backoff. Returns the final Response so each
// caller's existing status handling (ok / 403 export fall-through / throw) is
// unchanged - just after giving Drive time to recover. Honours AbortSignal
// (Reset mid-walk / -download) so a cancel doesn't wait out a backoff.
const driveFetch = async (
  url: string,
  accessToken: string,
  opts?: { signal?: AbortSignal; onRetry?: OnDriveRetry },
): Promise<Response> => {
  const init: RequestInit = {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal: opts?.signal,
  };
  let resp = await fetch(url, init);
  for (
    let attempt = 1;
    attempt < DRIVE_MAX_ATTEMPTS && isRetryableDriveStatus(resp.status);
    attempt++
  ) {
    if (opts?.signal?.aborted) break;
    const delayMs = driveRetryDelayMs(resp, attempt);
    opts?.onRetry?.({ attempt, maxAttempts: DRIVE_MAX_ATTEMPTS, delayMs, status: resp.status });
    await driveSleep(delayMs);
    resp = await fetch(url, init);
  }
  return resp;
};

export const listFolderChildren = async (
  folderId: string,
  accessToken: string,
  signal?: AbortSignal,
  onRetry?: OnDriveRetry,
): Promise<DriveFile[]> => {
  const params = new URLSearchParams({
    q: `'${folderId}' in parents and trashed=false`,
    fields: "files(id,name,mimeType,parents,shortcutDetails,size,modifiedTime)",
    pageSize: "200",
    supportsAllDrives: "true",
    includeItemsFromAllDrives: "true",
    corpora: "allDrives",
  });
  const resp = await driveFetch(
    `https://www.googleapis.com/drive/v3/files?${params}`,
    accessToken,
    { signal, onRetry },
  );
  if (!resp.ok) {
    throw new Error(`Drive folder list failed (${resp.status}): ${await resp.text()}`);
  }
  const data = (await resp.json()) as { files?: DriveFile[] };
  return data.files ?? [];
};

// Breadth-first walk. depth-capped so a pathological symlink loop
// or massive Drive can't lock the tab; visited-tracked so the same
// folder ID isn't expanded twice. Returns a flat list of files
// (no sub-folders).
//
// `listFn` takes a folder ID and returns its children. The caller
// is expected to bind in the access token + retry-on-401 logic
// once, then pass the bound function here. This keeps the
// retry boundary at the *leaf* of the walk: if a token expires
// halfway through, the retried single page call slots back into
// the walk at exactly the folder it left off in, rather than
// replaying the whole tree from the root. The shared `visited`
// set survives retries because it lives in this function's
// closure, not behind the retry wrapper.
//
// Optional `signal` is checked at the start of each folder
// expansion so Reset mid-walk halts promptly without waiting for
// the in-flight HTTP request to settle.
export const listFolderRecursively = async (
  folderId: string,
  listFn: (folderId: string) => Promise<DriveFile[]>,
  opts?: {
    depth?: number;
    visited?: Set<string>;
    signal?: AbortSignal;
    // Folder-name chain from the picked root down to this folder, used
    // to stamp each returned file's `path`. The caller seeds it with
    // the picked folder's name.
    pathPrefix?: string[];
  },
): Promise<DriveFile[]> => {
  const depth = opts?.depth ?? 0;
  const visited = opts?.visited ?? new Set<string>();
  const pathPrefix = opts?.pathPrefix ?? [];
  if (opts?.signal?.aborted) throw new DOMException("Aborted", "AbortError");
  if (depth > MAX_FOLDER_DEPTH) return [];
  if (visited.has(folderId)) return [];
  visited.add(folderId);

  const children = await listFn(folderId);
  const files: DriveFile[] = [];
  const subFolders: DriveFile[] = [];
  for (const child of children) {
    if (child.mimeType === FOLDER_MIME) subFolders.push(child);
    else files.push({ ...child, path: [...pathPrefix, child.name].join("/") });
  }
  for (const sub of subFolders) {
    const nested = await listFolderRecursively(sub.id, listFn, {
      depth: depth + 1,
      visited,
      signal: opts?.signal,
      pathPrefix: [...pathPrefix, sub.name],
    });
    files.push(...nested);
  }
  return files;
};

// ---------------------------------------------------------------------- //
// Classification
// ---------------------------------------------------------------------- //

export interface ClassifiedDriveFile extends DriveFile {
  _ext: string;
  _sizeNum: number | null;
}

export interface DriveClassification {
  toImport: ClassifiedDriveFile[];
  tooBig: ClassifiedDriveFile[];
  unsupported: ClassifiedDriveFile[];
  bytes: { toImport: number; tooBig: number; unsupported: number };
}

// Three buckets:
//   - toImport: agent-readable AND under cap
//   - tooBig: agent-readable but oversize
//   - unsupported: agent can't read this format
// Google-native types are unsized; assume they're always under
// the cap (their text/PDF export is bounded by Google's own 10 MB
// export limit, which downloadDriveFileAsBlob handles separately).
export const classifyDriveFiles = (
  files: DriveFile[],
  sizeCapMB: number = DEFAULT_SIZE_CAP_MB,
): DriveClassification => {
  const sizeCapBytes = sizeCapMB * 1024 * 1024;
  const toImport: ClassifiedDriveFile[] = [];
  const tooBig: ClassifiedDriveFile[] = [];
  const unsupported: ClassifiedDriveFile[] = [];
  for (const f of files) {
    const ext = (f.name ?? "").toLowerCase().split(".").pop() ?? "";
    const isReadable =
      AGENT_READABLE_EXTS.has(ext) || AGENT_READABLE_NATIVE_MIMES.has(f.mimeType);
    const sizeNum = f.size ? Number(f.size) : null;
    const annotated: ClassifiedDriveFile = { ...f, _ext: ext, _sizeNum: sizeNum };
    if (!isReadable) {
      unsupported.push(annotated);
    } else if (sizeNum != null && sizeNum > sizeCapBytes) {
      tooBig.push(annotated);
    } else {
      toImport.push(annotated);
    }
  }
  const sum = (xs: ClassifiedDriveFile[]): number =>
    xs.reduce((a, x) => a + (x._sizeNum ?? 0), 0);
  return {
    toImport,
    tooBig,
    unsupported,
    bytes: {
      toImport: sum(toImport),
      tooBig: sum(tooBig),
      unsupported: sum(unsupported),
    },
  };
};

export const formatBytesPretty = (n: number): string => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Per-bucket breakdown helper for the preview UI.
export const countByExt = (files: ClassifiedDriveFile[]): [string, number][] => {
  const counts: Record<string, number> = {};
  for (const f of files) {
    const key =
      f._ext ||
      (f.mimeType || "").replace("application/vnd.google-apps.", "g-") ||
      "unknown";
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
};

// ---------------------------------------------------------------------- //
// Download
// ---------------------------------------------------------------------- //

const resolveShortcut = async (
  doc: DriveFile,
  accessToken: string,
  signal?: AbortSignal,
  onRetry?: OnDriveRetry,
): Promise<DriveFile> => {
  const resp = await driveFetch(
    `https://www.googleapis.com/drive/v3/files/${doc.id}?fields=shortcutDetails,name&supportsAllDrives=true`,
    accessToken,
    { signal, onRetry },
  );
  if (!resp.ok) {
    throw new Error(`Shortcut resolve failed: ${await resp.text()}`);
  }
  const data = (await resp.json()) as DriveFile;
  const target = data.shortcutDetails;
  if (!target?.targetId) {
    throw new DriveSkippedError(`shortcut "${doc.name}" has no target`);
  }
  return {
    id: target.targetId,
    name: doc.name,
    mimeType: target.targetMimeType ?? "application/octet-stream",
  };
};

// Download one Drive file as a `File` ready for the existing
// /uploads/initiate pipeline. Three branches:
//   1. Shortcut -> recurse into the target.
//   2. Google-native -> try each export candidate in order,
//      falling through on `exportSizeLimitExceeded`.
//   3. Plain binary -> direct GET with alt=media.
//
// `signal` is forwarded to every fetch so that Reset mid-batch
// cancels the in-flight HTTP request rather than waiting for the
// whole blob to download first.
export const downloadDriveFileAsBlob = async (
  doc: DriveFile,
  accessToken: string,
  signal?: AbortSignal,
  onRetry?: OnDriveRetry,
): Promise<File> => {
  const mime = doc.mimeType;

  if (mime === "application/vnd.google-apps.shortcut") {
    const target = await resolveShortcut(doc, accessToken, signal, onRetry);
    return downloadDriveFileAsBlob(target, accessToken, signal, onRetry);
  }

  if (SKIP_EXPORT_TYPES.has(mime)) {
    throw new DriveSkippedError(
      `"${doc.name}" is a Google ${mime.replace("application/vnd.google-apps.", "")} — type can't be exported`,
    );
  }

  const isNative = mime?.startsWith("application/vnd.google-apps.");
  if (isNative) {
    const candidates = NATIVE_EXPORT_MAP[mime];
    if (!candidates) {
      throw new DriveSkippedError(
        `"${doc.name}" is a Google ${mime.replace("application/vnd.google-apps.", "")} — no export format wired`,
      );
    }
    let lastDetail = "";
    for (const candidate of candidates) {
      const url = `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=${encodeURIComponent(candidate.mime)}`;
      const resp = await driveFetch(url, accessToken, { signal, onRetry });
      if (resp.ok) {
        const blob = await resp.blob();
        const outName = doc.name.toLowerCase().endsWith(candidate.ext)
          ? doc.name
          : `${doc.name}${candidate.ext}`;
        return tagDriveFile(
          new File([blob], outName, { type: candidate.mime }),
          doc.id,
          { path: doc.path, modifiedTime: doc.modifiedTime },
        );
      }
      lastDetail = await resp.text();
      // exportSizeLimitExceeded (403) means the requested format
      // is too big - fall through to the next candidate. Any
      // other error is fatal; break out.
      if (resp.status !== 403 || !lastDetail.includes("exportSizeLimitExceeded")) {
        throw new Error(
          `Drive download failed for "${doc.name}" (${resp.status}): ${lastDetail}`,
        );
      }
    }
    throw new DriveSkippedError(
      `"${doc.name}" exceeded Google's export size limit on every available format — skipped`,
    );
  }

  // Non-native files: direct binary download via alt=media. No
  // export-size limit applies here, but the operator's local
  // Drive quota / file permissions can still fail the call.
  const url = `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media&supportsAllDrives=true`;
  const resp = await driveFetch(url, accessToken, { signal, onRetry });
  if (!resp.ok) {
    throw new Error(
      `Drive download failed for "${doc.name}" (${resp.status}): ${await resp.text()}`,
    );
  }
  const blob = await resp.blob();
  return tagDriveFile(
    new File([blob], doc.name, { type: mime || "application/octet-stream" }),
    doc.id,
    { path: doc.path, modifiedTime: doc.modifiedTime },
  );
};

// ---------------------------------------------------------------------- //
// Concurrent runner
// ---------------------------------------------------------------------- //

export interface ConcurrencyProgress<T> {
  done: number;
  total: number;
  lastName?: string;
  lastItem?: T;
}

// Generic worker-pool: run `fn` against `items` with at most
// `concurrency` in flight. Errors per item are collected, never
// thrown, so one broken file doesn't kill the whole batch.
// `onItem` fires after each task completes (used to drive the
// progress bar).
//
// `signal` is honoured in two ways:
//  1. Each worker checks `signal.aborted` between items and bails
//     out promptly rather than starting the next download.
//  2. The signal is also passed to `fn` so per-item fetches can
//     abort in flight (mid-blob-download) rather than running to
//     completion just to discard the result.
// Already-completed items remain in `results`; aborted items go
// into `errors` with an AbortError.
export const runWithConcurrency = async <T, R>(
  items: T[],
  fn: (item: T, signal?: AbortSignal) => Promise<R>,
  opts?: {
    concurrency?: number;
    onItem?: (p: ConcurrencyProgress<T> & { name?: string }) => void;
    signal?: AbortSignal;
  },
): Promise<{ results: R[]; errors: { item: T; error: Error }[]; aborted: boolean }> => {
  const concurrency = opts?.concurrency ?? 6;
  const queue: { idx: number; it: T }[] = items.map((it, idx) => ({ idx, it }));
  const results: (R | null)[] = new Array(items.length).fill(null);
  const errors: { item: T; error: Error }[] = [];
  let done = 0;
  const worker = async (): Promise<void> => {
    while (queue.length > 0) {
      if (opts?.signal?.aborted) return;
      const next = queue.shift();
      if (!next) return;
      const { idx, it } = next;
      try {
        results[idx] = await fn(it, opts?.signal);
      } catch (e) {
        errors.push({ item: it, error: e instanceof Error ? e : new Error(String(e)) });
      }
      done += 1;
      const name =
        typeof it === "object" && it !== null && "name" in it
          ? String((it as { name: unknown }).name ?? "")
          : "";
      opts?.onItem?.({ done, total: items.length, lastItem: it, name });
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length || 1) }, () => worker()),
  );
  return {
    results: results.filter((r): r is R => r !== null),
    errors,
    aborted: opts?.signal?.aborted === true,
  };
};

// ---------------------------------------------------------------------- //
// Refresh (re-fetch by Drive file id)
// ---------------------------------------------------------------------- //

// Re-fetch a Drive file by id and return a tagged File. Used by the
// "Refresh from Drive" action on the files-management page: the
// upload row stores the original drive_file_id from when it was
// imported, and the operator-clicks-refresh path re-runs the
// download (picking up any edits the operator has made in Drive
// since) before re-PUTting the bytes to S3.
//
// We need the file's MIME to decide which branch of
// downloadDriveFileAsBlob to take (native vs binary), so the
// helper does a one-shot metadata fetch first.
export const refetchDriveFileById = async (
  driveFileId: string,
  accessToken: string,
  signal?: AbortSignal,
  onRetry?: OnDriveRetry,
): Promise<File> => {
  const metaResp = await driveFetch(
    `https://www.googleapis.com/drive/v3/files/${driveFileId}?fields=id,name,mimeType,size&supportsAllDrives=true`,
    accessToken,
    { signal, onRetry },
  );
  if (!metaResp.ok) {
    throw new Error(
      `Drive metadata fetch failed for id=${driveFileId} (${metaResp.status}): ${await metaResp.text()}`,
    );
  }
  const meta = (await metaResp.json()) as DriveFile;
  return downloadDriveFileAsBlob(meta, accessToken, signal, onRetry);
};
